<template>
    <div class="pd">
        <UserTopNav :active="'projects'" />

        <div class="pd-container">
            <!-- ═══ Back link ═══ -->
            <router-link to="/projects" class="pd-back">
                <font-awesome-icon icon="fa-solid fa-arrow-left" />
                <span>Proiecte</span>
            </router-link>

            <!-- ═══ Project header ═══ -->
            <div class="pd-header">
                <div class="pd-header__info">
                    <h1 class="pd-header__name">{{ currentProjectName || '...' }}</h1>
                    <div class="pd-header__meta" v-if="scopedProjectData">
                        <span class="pd-meta-tag">
                            <font-awesome-icon icon="fa-solid fa-calendar-days" />
                            {{ scopedProjectData.project_date ? formatDate(scopedProjectData.project_date) : '—' }}
                        </span>
                        <span class="pd-meta-tag pd-meta-tag--type">
                            {{ scopedProjectData.template_type === 'simple' ? 'Simple' : 'Complex' }}
                        </span>
                    </div>
                </div>
                <div class="pd-header__actions" v-if="scopedProjectData">
                    <a
                        v-if="scopedProjectData.template_type == 'simple'"
                        class="pd-btn pd-btn--rainbow"
                        target="_blank"
                        :href="`${getURL()}/#/ps/${scopedProjectData.project_id}/${slugify(scopedProjectData.project_name)}`"
                    >
                        <font-awesome-icon icon="fa-solid fa-wand-magic-sparkles" />
                        <span>Editeaza Invitatia</span>
                    </a>
                    <a
                        v-else
                        class="pd-btn pd-btn--rainbow"
                        target="_blank"
                        :href="`${getURL()}/#/public/${scopedProjectData.project_id}/edit`"
                    >
                        <font-awesome-icon icon="fa-solid fa-wand-magic-sparkles" />
                        <span>Editeaza Invitatia</span>
                    </a>
                </div>
            </div>

            <!-- ═══ Primary row: Invitati + Statistici ═══ -->
            <div class="pd-cards__row" v-if="scopedProjectData">
                <router-link
                    v-if="scopedProjectData.template_type !== 'simple'"
                    class="pd-card pd-card--primary"
                    :to="{
                        name: 'ProjectDetailsInvitations',
                        params: {
                            projectId: currentProjectId,
                            projectSlug: currentProjectSlug,
                        },
                    }"
                >
                    <div class="pd-card__icon pd-card__icon--blue">
                        <font-awesome-icon icon="fa-solid fa-users-gear" />
                    </div>
                    <div class="pd-card__content">
                        <h2 class="pd-card__title">Invitati</h2>
                        <p class="pd-card__desc">Adauga, modifica sau sterge invitatii din lista ta</p>
                    </div>
                    <font-awesome-icon icon="fa-solid fa-chevron-right" class="pd-card__arrow" />
                </router-link>

                <router-link
                    class="pd-card pd-card--primary"
                    :to="{
                        name: 'ProjectDetailsStats',
                        params: {
                            projectId: currentProjectId,
                            projectSlug: currentProjectSlug,
                        },
                    }"
                >
                    <div class="pd-card__icon pd-card__icon--green">
                        <font-awesome-icon icon="fa-solid fa-chart-column" />
                    </div>
                    <div class="pd-card__content">
                        <h2 class="pd-card__title">Statistici</h2>
                        <p class="pd-card__desc">Vezi raspunsurile invitatilor in timp real</p>
                    </div>
                    <font-awesome-icon icon="fa-solid fa-chevron-right" class="pd-card__arrow" />
                </router-link>
            </div>

            <!-- ═══ Send Invitations section ═══ -->
            <section class="pd-send" v-if="scopedProjectData && scopedProjectData.template_type != 'simple' && user">
                <div class="pd-send__header">
                    <div class="pd-send__icon">
                        <font-awesome-icon icon="fa-solid fa-paper-plane" />
                    </div>
                    <div>
                        <h2 class="pd-send__title">Trimite invitatiile</h2>
                        <p class="pd-send__desc">Selecteaza canalul de trimitere si personalizeaza mesajele.</p>
                    </div>
                </div>

                <div class="pd-send__warning">
                    <font-awesome-icon icon="fa-solid fa-triangle-exclamation" class="pd-send__warning-icon" />
                    <span>La acest tip de proiect, fiecare invitație este unică și personalizată. Trimite fiecare link doar persoanei pentru care a fost generat. Dacă link-ul ajunge la altcineva, răspunsul va fi înregistrat în locul invitatului original.</span>
                </div>

                <div class="pd-send__grid">
                    <div class="pd-send__col">
                        <button
                            class="pd-send__card"
                            @click="openSendEmailModal()"
                            :disabled="!canSendEmail || isEmailJobActive || user.user_active != 1 || plan.subscription_status != 'active'"
                        >
                            <div class="pd-send__card-icon pd-send__card-icon--email">
                                <font-awesome-icon icon="fa-solid fa-envelope" />
                            </div>
                            <div class="pd-send__card-body">
                                <div class="pd-send__card-title">Trimite prin Email</div>
                                <div class="pd-send__card-hint" v-if="canSendEmail">Selecteaza invitatii si trimite prin email</div>
                                <div class="pd-send__card-hint pd-send__card-hint--locked" v-else>
                                    <font-awesome-icon icon="fa-solid fa-lock" class="mr-1" />
                                    Indisponibil pentru planul curent
                                </div>
                            </div>
                            <div class="pd-send__badge" v-if="canSendEmail">
                                <font-awesome-icon icon="fa-solid fa-coins" />
                                <span>{{ credit ? credit.email_credits : 0 }}</span>
                            </div>
                            <span class="loading loading-bars loading-sm" v-if="isEmailJobActive"></span>
                            <font-awesome-icon v-else icon="fa-solid fa-chevron-right" class="pd-send__arrow" />
                        </button>
                        <button class="pd-customize" @click="isEmailMessageModalOpen = true" v-if="canSendEmail">
                            <font-awesome-icon icon="fa-solid fa-pen" />
                            <span>Personalizeaza mesajul Email</span>
                        </button>
                    </div>

                    <div class="pd-send__col">
                        <button
                            class="pd-send__card"
                            @click="openSendSMSModal()"
                            :disabled="!canSendSMS || isSMSJobActive || user.user_active != 1 || plan.subscription_status != 'active'"
                        >
                            <div class="pd-send__card-icon pd-send__card-icon--sms">
                                <font-awesome-icon icon="fa-solid fa-comment-sms" />
                            </div>
                            <div class="pd-send__card-body">
                                <div class="pd-send__card-title">Trimite prin SMS</div>
                                <div class="pd-send__card-hint" v-if="canSendSMS">Selecteaza invitatii si trimite prin SMS</div>
                                <div class="pd-send__card-hint pd-send__card-hint--locked" v-else>
                                    <font-awesome-icon icon="fa-solid fa-lock" class="mr-1" />
                                    Indisponibil pentru planul curent
                                </div>
                            </div>
                            <div class="pd-send__badge" v-if="canSendSMS">
                                <font-awesome-icon icon="fa-solid fa-coins" />
                                <span>{{ credit ? credit.sms_credits : 0 }}</span>
                            </div>
                            <span class="loading loading-bars loading-sm" v-if="isSMSJobActive"></span>
                            <font-awesome-icon v-else icon="fa-solid fa-chevron-right" class="pd-send__arrow" />
                        </button>
                        <button class="pd-customize" @click="isSMSMessageModalOpen = true" v-if="canSendSMS">
                            <font-awesome-icon icon="fa-solid fa-pen" />
                            <span>Personalizeaza mesajul SMS</span>
                        </button>
                    </div>

                    <div class="pd-send__col">
                        <button
                            class="pd-send__card"
                            @click="isSendWhatsAppModalOpen = true"
                            :disabled="!canSendWhatsApp || user.user_active != 1 || plan.subscription_status != 'active'"
                        >
                            <div class="pd-send__card-icon pd-send__card-icon--whatsapp">
                                <font-awesome-icon icon="fa-brands fa-whatsapp" />
                            </div>
                            <div class="pd-send__card-body">
                                <div class="pd-send__card-title">Trimite prin WhatsApp</div>
                                <div class="pd-send__card-hint" v-if="canSendWhatsApp">Deschide conversatia cu fiecare invitat</div>
                                <div class="pd-send__card-hint pd-send__card-hint--locked" v-else>
                                    <font-awesome-icon icon="fa-solid fa-lock" class="mr-1" />
                                    Indisponibil pentru planul curent
                                </div>
                            </div>
                            <div class="pd-send__badge pd-send__badge--free" v-if="canSendWhatsApp">
                                Gratuit
                            </div>
                            <font-awesome-icon icon="fa-solid fa-chevron-right" class="pd-send__arrow" />
                        </button>
                        <button class="pd-customize" @click="isWhatsAppMessageModalOpen = true" v-if="canSendWhatsApp">
                            <font-awesome-icon icon="fa-solid fa-pen" />
                            <span>Personalizeaza mesajul WhatsApp</span>
                        </button>
                    </div>
                </div>
            </section>

            <!-- ═══ Automatic Reminders section ═══ -->
            <section class="pd-remind" v-if="scopedProjectData && scopedProjectData.template_type != 'simple' && user">
                <div class="pd-remind__header">
                    <div class="pd-remind__icon">
                        <font-awesome-icon icon="fa-solid fa-bell" />
                    </div>
                    <div class="pd-remind__header-text">
                        <h2 class="pd-remind__title">Remindere automate</h2>
                        <p class="pd-remind__desc">Trimite automat un reminder invitaților care nu au răspuns încă.</p>
                    </div>
                </div>

                <!-- Loading state -->
                <div v-if="reminderLoading" class="pd-remind__loading">
                    <span class="loading loading-spinner loading-sm"></span>
                    <span>Se încarcă...</span>
                </div>

                <template v-else>
                    <!-- Add new reminder form -->
                    <div class="pd-remind__add">
                        <div class="pd-remind__add-row">
                            <label class="pd-remind__field">
                                <span class="pd-remind__label">Trimite cu</span>
                                <select v-model="newReminder.days_before" class="select select-bordered select-sm">
                                    <option :value="1">1 zi</option>
                                    <option :value="3">3 zile</option>
                                    <option :value="5">5 zile</option>
                                    <option :value="7">7 zile</option>
                                    <option :value="14">14 zile</option>
                                    <option :value="21">21 zile</option>
                                    <option :value="30">30 zile</option>
                                </select>
                                <span class="pd-remind__label">înainte de eveniment</span>
                            </label>

                            <label class="pd-remind__field">
                                <span class="pd-remind__label">Canal</span>
                                <select v-model="newReminder.channel" class="select select-bordered select-sm">
                                    <option value="email">Email</option>
                                    <option value="sms">SMS</option>
                                    <option value="both">Email + SMS</option>
                                </select>
                            </label>

                            <button
                                class="btn btn-primary btn-sm"
                                @click="addReminder"
                                :disabled="reminderSaving"
                            >
                                <font-awesome-icon icon="fa-solid fa-plus" />
                                Adaugă reminder
                            </button>
                        </div>

                        <!-- Credit estimate (only when reminders exist) -->
                        <div class="pd-remind__estimate" v-if="reminderConfigs.length > 0 && reminderPendingCount > 0">
                            <font-awesome-icon icon="fa-solid fa-coins" class="pd-remind__estimate-icon" />
                            <span>
                                <strong>{{ reminderPendingCount }}</strong> invitați fără răspuns.
                                Cost estimat per reminder:
                                <template v-if="newReminder.channel === 'email' || newReminder.channel === 'both'">
                                    <strong>{{ reminderPendingCount }} credite email</strong>
                                    (disponibile: {{ credit ? credit.email_credits : 0 }})
                                </template>
                                <template v-if="newReminder.channel === 'both'"> + </template>
                                <template v-if="newReminder.channel === 'sms' || newReminder.channel === 'both'">
                                    <strong>{{ reminderPendingCount }} credite SMS</strong>
                                    (disponibile: {{ credit ? credit.sms_credits : 0 }})
                                </template>
                            </span>
                        </div>
                        <div class="pd-remind__estimate pd-remind__estimate--ok" v-else-if="reminderConfigs.length > 0 && reminderLoaded && reminderPendingCount === 0">
                            <font-awesome-icon icon="fa-solid fa-circle-check" />
                            <span>Toți invitații au răspuns — nu sunt remindere necesare.</span>
                        </div>
                    </div>

                    <!-- Existing reminders list -->
                    <div class="pd-remind__list" v-if="reminderConfigs.length > 0">
                        <div
                            class="pd-remind__item"
                            v-for="config in reminderConfigs"
                            :key="config.id"
                        >
                            <div class="pd-remind__item-info">
                                <div class="pd-remind__item-timing">
                                    <font-awesome-icon icon="fa-solid fa-clock" class="pd-remind__item-clock" />
                                    <strong>{{ config.days_before }} {{ config.days_before === 1 ? 'zi' : 'zile' }}</strong> înainte
                                </div>
                                <div class="pd-remind__item-channel">
                                    <span class="pd-remind__channel-badge" :class="'pd-remind__channel-badge--' + config.channel">
                                        {{ config.channel === 'both' ? 'Email + SMS' : config.channel === 'email' ? 'Email' : 'SMS' }}
                                    </span>
                                </div>
                                <div class="pd-remind__item-date" v-if="getReminderSendDate(config)">
                                    Trimite pe {{ getReminderSendDate(config) }}
                                </div>
                            </div>
                            <div class="pd-remind__item-actions">
                                <label class="pd-remind__toggle">
                                    <input
                                        type="checkbox"
                                        class="toggle toggle-sm toggle-primary"
                                        :checked="config.enabled"
                                        @change="toggleReminder(config)"
                                    />
                                </label>
                                <button
                                    class="btn btn-ghost btn-square btn-sm text-error"
                                    @click="deleteReminder(config)"
                                    data-tip="Șterge"
                                >
                                    <font-awesome-icon icon="fa-solid fa-trash-can" />
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Info text -->
                    <p class="pd-remind__info">
                        <font-awesome-icon icon="fa-solid fa-circle-info" class="text-blue-400" />
                        Vei primi un email de notificare cu 24h înainte de trimiterea fiecărui reminder, cu detalii despre cost și sold credite. Poți anula oricând.
                    </p>
                </template>
            </section>

            <!-- Inactive project banner -->
            <div
                v-if="scopedProjectData && scopedProjectData.active == 0"
                class="pd-draft-banner"
            >
                <div class="pd-draft-banner__icon">
                    <font-awesome-icon icon="fa-solid fa-lock" />
                </div>
                <div class="pd-draft-banner__content">
                    <h3 class="pd-draft-banner__title">Proiect inactiv</h3>
                    <p class="pd-draft-banner__desc">
                        Invitatia nu poate fi partajata public. Activeaza un plan pentru a face proiectul accesibil invitatilor tai.
                    </p>
                </div>
                <router-link to="/pachete" class="pd-draft-banner__btn">
                    <font-awesome-icon icon="fa-solid fa-bolt" />
                    <span>Activeaza</span>
                </router-link>
            </div>

            <!-- Simple project: unique link -->
            <div
                v-if="scopedProjectData && scopedProjectData.template_type == 'simple'"
                class="pd-link-card"
                :class="{ 'pd-link-card--disabled': scopedProjectData.active == 0 }"
            >
                <div class="pd-link-card__header">
                    <div class="pd-link-card__icon">
                        <font-awesome-icon icon="fa-solid fa-link" />
                    </div>
                    <div>
                        <h2 class="pd-link-card__title">Link unic invitatie</h2>
                        <p class="pd-link-card__desc" v-if="scopedProjectData.active == 1">Copiaza link-ul si trimite-l invitatilor tai.</p>
                        <p class="pd-link-card__desc" v-else>Link-ul va deveni functional dupa activarea proiectului.</p>
                    </div>
                </div>
                <div class="pd-link-card__body">
                    <div class="pd-link-card__url">{{ getURL() }}/#/ps/{{ scopedProjectData.project_id }}/{{ slugify(scopedProjectData.project_name) }}</div>
                    <button
                        class="pd-btn pd-btn--copy"
                        :disabled="scopedProjectData.active == 0"
                        @click="copyToClipboard(`${getURL()}/#/ps/${scopedProjectData.project_id}/${slugify(scopedProjectData.project_name)}`)"
                    >
                        <font-awesome-icon icon="fa-solid fa-copy" />
                        <span>Copiaza Link</span>
                    </button>
                </div>
            </div>

            <!-- ═══ Secondary: Configuratii ═══ -->
            <router-link
                v-if="scopedProjectData"
                class="pd-card pd-card--secondary"
                :to="{
                    name: 'ProjectDetailsConfig',
                    params: {
                        projectId: currentProjectId,
                        projectSlug: currentProjectSlug,
                    },
                }"
            >
                <div class="pd-card__icon pd-card__icon--slate">
                    <font-awesome-icon icon="fa-solid fa-sliders" />
                </div>
                <div class="pd-card__content">
                    <h2 class="pd-card__title">Configuratii</h2>
                    <p class="pd-card__desc">Detalii eveniment, setari proiect</p>
                </div>
                <font-awesome-icon icon="fa-solid fa-chevron-right" class="pd-card__arrow" />
            </router-link>

            <!-- ═══ Export Data ═══ -->
            <button
                v-if="scopedProjectData && !scopedProjectData.gdpr_purged_at && scopedProjectData.template_type !== 'simple'"
                class="pd-card pd-card--secondary"
                @click="exportData"
                :disabled="exporting"
            >
                <div class="pd-card__icon pd-card__icon--amber">
                    <font-awesome-icon :icon="exporting ? 'fa-solid fa-spinner' : 'fa-solid fa-file-arrow-down'" :spin="exporting" />
                </div>
                <div class="pd-card__content text-left">
                    <h2 class="pd-card__title">Descarcă datele invitaților</h2>
                    <p class="pd-card__desc">Exportă lista de invitați și răspunsuri RSVP în format CSV</p>
                </div>
                <font-awesome-icon icon="fa-solid fa-chevron-right" class="pd-card__arrow" />
            </button>

            <!-- ═══ GDPR purged notice ═══ -->
            <div v-if="scopedProjectData && scopedProjectData.gdpr_purged_at" class="pd-notice pd-notice--gdpr">
                <font-awesome-icon icon="fa-solid fa-shield-halved" class="pd-notice__icon" />
                <div>
                    <strong>Datele invitaților au fost șterse</strong>
                    <p class="pd-notice__text">Conform politicii GDPR, datele personale ale invitaților au fost eliminate automat după încheierea evenimentului. Designul invitației rămâne disponibil.</p>
                </div>
            </div>
        </div>

        <!-- ═══ Send Email Modal ═══ -->
        <ModalComponent
            v-if="credit && isSendEmailModalOpen"
            :is-open="isSendEmailModalOpen"
            :containerClass="'w-11/12 max-w-5xl max-w-[calc(100vw-1rem)]'"
            @close="closeSendEmailModal"
        >
            <h3 class="font-bold text-lg">Trimite invitatia prin Email</h3>
            <GetParticipantsListSendEmail
                v-if="isSendEmailModalOpen"
                :projectId="currentProjectId"
                :closeSendEmailModal="closeSendEmailModal"
                :isSendEmailModalOpen="isSendEmailModalOpen"
                :setEmailJobStatus="setEmailJobStatus"
            />
            <div class="modal-action">
                <form method="dialog">
                    <button
                        @click="closeSendEmailModal"
                        class="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
                    >
                        ✕
                    </button>
                </form>
            </div>
        </ModalComponent>

        <!-- ═══ Send SMS Modal ═══ -->
        <ModalComponent
            :is-open="isSendSMSModalOpen"
            :containerClass="'w-11/12 max-w-5xl max-w-[calc(100vw-1rem)]'"
            @close="closeSendSMSModal"
            v-if="credit && isSendSMSModalOpen"
        >
            <h3 class="font-bold text-lg">Trimite invitatia prin SMS</h3>
            <GetParticipantsListSendSMS
                v-if="isSendSMSModalOpen"
                :projectId="currentProjectId"
                :closeSendSMSModal="closeSendSMSModal"
                :isSendSMSModalOpen="isSendSMSModalOpen"
                :setSMSJobStatus="setSMSJobStatus"
            />
            <div class="modal-action">
                <form method="dialog">
                    <button
                        @click="closeSendSMSModal"
                        class="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
                    >
                        ✕
                    </button>
                </form>
            </div>
        </ModalComponent>

        <!-- ═══ Send WhatsApp Modal ═══ -->
        <ModalComponent
            v-if="isSendWhatsAppModalOpen"
            :is-open="isSendWhatsAppModalOpen"
            :containerClass="'w-11/12 max-w-3xl'"
            @close="isSendWhatsAppModalOpen = false"
        >
            <h3 class="font-bold text-lg">
                <font-awesome-icon icon="fa-brands fa-whatsapp" class="text-green-500 mr-1" />
                Trimite invitatia prin WhatsApp
            </h3>
            <GetParticipantsListSendWhatsApp
                v-if="isSendWhatsAppModalOpen"
                :projectId="currentProjectId"
                :isSendWhatsAppModalOpen="isSendWhatsAppModalOpen"
            />
            <div class="modal-action">
                <form method="dialog">
                    <button
                        @click="isSendWhatsAppModalOpen = false"
                        class="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
                    >
                        ✕
                    </button>
                </form>
            </div>
        </ModalComponent>

        <!-- ═══ Email Message Customization Modal ═══ -->
        <ModalComponent
            v-if="isEmailMessageModalOpen && scopedProjectData"
            :is-open="isEmailMessageModalOpen"
            :containerClass="'w-11/12 max-w-4xl'"
            @close="isEmailMessageModalOpen = false"
        >
            <div class="pd-modal__header">
                <div class="pd-modal__icon pd-modal__icon--email">
                    <font-awesome-icon icon="fa-solid fa-envelope" />
                </div>
                <div>
                    <h3 class="pd-modal__title">Mesaj invitatie Email</h3>
                    <p class="pd-modal__desc">Personalizeaza mesajul si vezi cum va arata in inbox-ul invitatilor.</p>
                </div>
            </div>
            <InvitationMessage
                v-if="scopedProjectData.invitation_message_email"
                :projectId="scopedProjectData.project_id"
                :projectName="scopedProjectData.project_name"
                :projectSlug="scopedProjectData.project_slug"
                :nameInURL="scopedProjectData.name_in_url"
                :template_type="scopedProjectData.template_type"
                :userMessage="scopedProjectData.invitation_message_email"
                :projectDate="scopedProjectData.project_date"
                :isEmailScope="true"
            />
            <div class="pd-modal__test" v-if="user && user.phone_number && currentProjectId">
                <TestEmailComponent
                    :userEmail="user.email"
                    :projectID="currentProjectId"
                    :isDisabled="
                        isEmailJobActive == true ||
                        user.user_active != 1 ||
                        plan.subscription_status != 'active' ||
                        scopedProjectData.template_type == 'simple'
                    "
                ></TestEmailComponent>
            </div>
            <div class="modal-action">
                <form method="dialog">
                    <button
                        @click="isEmailMessageModalOpen = false"
                        class="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
                    >
                        ✕
                    </button>
                </form>
            </div>
        </ModalComponent>

        <!-- ═══ SMS Message Customization Modal ═══ -->
        <ModalComponent
            v-if="isSMSMessageModalOpen && scopedProjectData"
            :is-open="isSMSMessageModalOpen"
            :containerClass="'w-11/12 max-w-4xl'"
            @close="isSMSMessageModalOpen = false"
        >
            <div class="pd-modal__header">
                <div class="pd-modal__icon pd-modal__icon--sms">
                    <font-awesome-icon icon="fa-solid fa-comment-sms" />
                </div>
                <div>
                    <h3 class="pd-modal__title">Mesaj invitatie SMS</h3>
                    <p class="pd-modal__desc">Personalizeaza mesajul si vezi cum va arata pe telefonul invitatilor.</p>
                </div>
            </div>
            <InvitationMessage
                v-if="scopedProjectData.invitation_message"
                :projectId="scopedProjectData.project_id"
                :projectName="scopedProjectData.project_name"
                :projectSlug="scopedProjectData.project_slug"
                :nameInURL="scopedProjectData.name_in_url"
                :template_type="scopedProjectData.template_type"
                :userMessage="scopedProjectData.invitation_message"
            />
            <div class="pd-modal__test" v-if="user && user.phone_number && currentProjectId">
                <TestSMSComponent
                    :userMobile="user.phone_number"
                    :projectID="currentProjectId"
                    :isDisabled="
                        isSMSJobActive == true ||
                        user.user_active != 1 ||
                        plan.subscription_status != 'active' ||
                        scopedProjectData.template_type == 'simple'
                    "
                ></TestSMSComponent>
            </div>
            <div class="modal-action">
                <form method="dialog">
                    <button
                        @click="isSMSMessageModalOpen = false"
                        class="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
                    >
                        ✕
                    </button>
                </form>
            </div>
        </ModalComponent>

        <!-- ═══ WhatsApp Message Customization Modal ═══ -->
        <ModalComponent
            v-if="isWhatsAppMessageModalOpen && scopedProjectData"
            :is-open="isWhatsAppMessageModalOpen"
            :containerClass="'w-11/12 max-w-4xl'"
            @close="isWhatsAppMessageModalOpen = false"
        >
            <div class="pd-modal__header">
                <div class="pd-modal__icon pd-modal__icon--whatsapp">
                    <font-awesome-icon icon="fa-brands fa-whatsapp" />
                </div>
                <div>
                    <h3 class="pd-modal__title">Mesaj invitatie WhatsApp</h3>
                    <p class="pd-modal__desc">Personalizeaza mesajul si vezi cum va arata in conversatia WhatsApp.</p>
                </div>
            </div>
            <InvitationMessage
                :projectId="scopedProjectData.project_id"
                :projectName="scopedProjectData.project_name"
                :projectSlug="scopedProjectData.project_slug"
                :nameInURL="scopedProjectData.name_in_url"
                :template_type="scopedProjectData.template_type"
                :userMessage="scopedProjectData.invitation_message_whatsapp || scopedProjectData.invitation_message || ''"
                :isWhatsAppScope="true"
            />
            <div class="modal-action">
                <form method="dialog">
                    <button
                        @click="isWhatsAppMessageModalOpen = false"
                        class="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
                    >
                        ✕
                    </button>
                </form>
            </div>
        </ModalComponent>

        <!-- ═══ Delete Reminder Confirmation Modal ═══ -->
        <ModalComponent
            :is-open="!!reminderToDelete"
            :containerClass="'w-11/12 max-w-md'"
            @close="reminderToDelete = null"
        >
            <div class="p-6">
                <div class="pd-modal__header">
                    <div class="pd-remind__icon">
                        <font-awesome-icon icon="fa-solid fa-trash-can" />
                    </div>
                    <div>
                        <h3 class="pd-modal__title">Șterge reminder</h3>
                        <p class="pd-modal__desc">Ești sigur că vrei să ștergi acest reminder?</p>
                    </div>
                </div>
                <div v-if="reminderToDelete" style="padding: 12px 16px; border-radius: 10px; background: #f8fafc; border: 1px solid #e2e8f0; margin-bottom: 20px; font-size: 13px; color: #334155;">
                    <strong>{{ reminderToDelete.days_before }} zile</strong> înainte de eveniment
                    ·
                    <span style="text-transform: uppercase; font-weight: 700; font-size: 11px;"
                        :style="{ color: reminderToDelete.channel === 'email' ? '#2563eb' : reminderToDelete.channel === 'sms' ? '#16a34a' : '#7c3aed' }">
                        {{ reminderToDelete.channel === 'both' ? 'Email + SMS' : reminderToDelete.channel.toUpperCase() }}
                    </span>
                </div>
                <div style="display: flex; justify-content: flex-end; gap: 10px;">
                    <button class="btn btn-sm btn-ghost" @click="reminderToDelete = null">Anulează</button>
                    <button class="btn btn-sm btn-error text-white" @click="confirmDeleteReminder" :disabled="reminderSaving">
                        <font-awesome-icon v-if="reminderSaving" icon="fa-solid fa-spinner" spin class="mr-1" />
                        Șterge
                    </button>
                </div>
            </div>
        </ModalComponent>

        <!-- Loading overlay -->
        <div v-if="loading" class="pd-loading">
            <span class="loading loading-spinner loading-lg"></span>
        </div>
    </div>
</template>

<script>
import UserTopNav from '@/components/UserTopNav.vue';
import ModalComponent from '@/components/ModalComponent.vue';
import GetParticipantsListSendEmail from '@/components/GetParticipantsListSendEmail.vue';
import GetParticipantsListSendSMS from '@/components/GetParticipantsListSendSMS.vue';
import GetParticipantsListSendWhatsApp from '@/components/GetParticipantsListSendWhatsApp.vue';
import InvitationMessage from '@/components/InvitationMessage.vue';
import TestEmailComponent from '@/components/TestEmailComponent.vue';
import TestSMSComponent from '@/components/TestSMSComponent.vue';
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { mapGetters, mapActions } from 'vuex';
import { hasModuleAccess } from '@/composables/useModuleAccess';

export default {
    name: 'ProjectDetails',
    components: {
        UserTopNav,
        ModalComponent,
        GetParticipantsListSendEmail,
        GetParticipantsListSendSMS,
        GetParticipantsListSendWhatsApp,
        InvitationMessage,
        TestEmailComponent,
        TestSMSComponent,
    },
    props: {
        projectId: {},
        projectName: {
            type: String,
        },
        projectSlug: {
            type: String,
        },
    },
    data() {
        return {
            currentProjectId: null,
            isSendEmailModalOpen: false,
            isSendSMSModalOpen: false,
            isEmailJobActive: false,
            isSMSJobActive: false,
            isEmailMessageModalOpen: false,
            isSMSMessageModalOpen: false,
            isSendWhatsAppModalOpen: false,
            isWhatsAppMessageModalOpen: false,
            exporting: false,
            // Delivery module access
            canSendEmail: false,
            canSendSMS: false,
            canSendWhatsApp: false,
            // Reminder system
            reminderConfigs: [],
            reminderPendingCount: 0,
            reminderLoading: false,
            reminderLoaded: false,
            reminderSaving: false,
            reminderToDelete: null,
            newReminder: {
                days_before: 7,
                channel: 'email',
            },
        };
    },
    setup() {
        const toast = useToast();
        return { toast };
    },
    computed: {
        ...mapGetters(['user', 'projects', 'credit', 'plan', 'loading', 'scopedProjectData']),
        currentProjectName() {
            if (this.scopedProjectData && this.scopedProjectData.project_name) {
                return this.scopedProjectData.project_name;
            }
            return this.$route.params.projectSlug || '...';
        },
        currentProjectSlug() {
            if (this.scopedProjectData && this.scopedProjectData.project_slug) {
                return this.scopedProjectData.project_slug;
            }
            return this.$route.params.projectSlug || '';
        },
    },
    created() {
        this.currentProjectId = this.$route.params.projectId;
    },
    mounted() {
        if (!this.projects) {
            this.fetchUserDetails();
        }
        this.$store.dispatch('fetchUserDetails');
        const projectId = this.$route.params.projectId;
        if (!this.scopedProjectData || this.scopedProjectData.project_id != projectId) {
            this.$store.dispatch('fetchProjectUsers', {
                projectId,
                options: {},
                participants: true,
            });
        }
        this.loadReminderConfigs();
        this.checkDeliveryAccess();
    },
    methods: {
        ...mapActions(['fetchUserDetails']),
        slugify(str) {
            return (str || '')
                .toLowerCase()
                .trim()
                .replace(/[^a-z0-9-]+/g, '-')
                .replace(/^-+|-+$/g, '');
        },
        getURL() {
            const url = new URL(window.location.href);
            return url.origin;
        },
        formatDate(dateString) {
            if (!dateString) return '—';
            const months = ['Ian', 'Feb', 'Mar', 'Apr', 'Mai', 'Iun', 'Iul', 'Aug', 'Sep', 'Oct', 'Noi', 'Dec'];
            const parts = dateString.split('-');
            if (parts.length < 3) return dateString;
            const month = months[parseInt(parts[1], 10) - 1];
            return `${parseInt(parts[2], 10)} ${month} ${parts[0]}`;
        },
        copyToClipboard(textToCopy) {
            const el = document.createElement('textarea');
            el.value = textToCopy;
            document.body.appendChild(el);
            el.select();
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard
                    .writeText(textToCopy)
                    .then(() => {
                        this.toast.success('Link-ul invitatiei a fost copiat!');
                    })
                    .catch((err) => {
                        console.error('Failed to copy text: ', err);
                    });
            } else {
                try {
                    document.execCommand('copy');
                    this.toast.success('Link-ul invitatiei a fost copiat!');
                } catch (err) {
                    console.error('Fallback: Failed to copy text: ', err);
                }
            }
            document.body.removeChild(el);
        },
        async exportData() {
            this.exporting = true;
            try {
                const jwtToken = localStorage.getItem('userToken');
                const response = await fetch(
                    `/api/index.php?endpoint=exportProjectData&projectId=${this.currentProjectId}`,
                    { headers: { Authorization: `Bearer ${jwtToken}` } }
                );
                if (!response.ok) throw new Error('Export failed');
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `export_${this.currentProjectId}_${new Date().toISOString().slice(0,10)}.csv`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
                this.toast.success('Datele au fost descărcate!');
            } catch (e) {
                this.toast.error('Eroare la descărcarea datelor.');
                console.error(e);
            } finally {
                this.exporting = false;
            }
        },
        async checkDeliveryAccess() {
            try {
                const [email, sms, whatsapp] = await Promise.all([
                    hasModuleAccess('email_delivery'),
                    hasModuleAccess('sms_delivery'),
                    hasModuleAccess('whatsapp_delivery'),
                ]);
                this.canSendEmail = email;
                this.canSendSMS = sms;
                this.canSendWhatsApp = whatsapp;
            } catch (e) {
                console.error('Error checking delivery access:', e);
            }
        },
        openSendEmailModal() { this.isSendEmailModalOpen = true; },
        closeSendEmailModal() { this.isSendEmailModalOpen = false; },
        setEmailJobStatus(status) { this.isEmailJobActive = status; },
        openSendSMSModal() { this.isSendSMSModalOpen = true; },
        closeSendSMSModal() { this.isSendSMSModalOpen = false; },
        setSMSJobStatus(status) { this.isSMSJobActive = status; },

        // ─── Reminder methods ───
        async loadReminderConfigs() {
            if (!this.currentProjectId) return;
            this.reminderLoading = true;
            try {
                const jwtToken = localStorage.getItem('userToken');
                const res = await axios.get(
                    `/api/index.php?endpoint=getReminderConfig&project_id=${this.currentProjectId}`,
                    { headers: { Authorization: `Bearer ${jwtToken}` } }
                );
                if (res.data.success) {
                    this.reminderConfigs = res.data.configs;
                    this.reminderPendingCount = res.data.pending_invitees;
                    this.reminderLoaded = true;
                }
            } catch (e) {
                console.error('Error loading reminder configs:', e);
            } finally {
                this.reminderLoading = false;
            }
        },
        async addReminder() {
            this.reminderSaving = true;
            try {
                const jwtToken = localStorage.getItem('userToken');
                const res = await axios.post(
                    '/api/index.php?endpoint=saveReminderConfig',
                    {
                        project_id: this.currentProjectId,
                        action: 'upsert',
                        days_before: this.newReminder.days_before,
                        channel: this.newReminder.channel,
                        enabled: true,
                    },
                    { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwtToken}` } }
                );
                if (res.data.success) {
                    this.toast.success('Reminder adăugat!');
                    await this.loadReminderConfigs();
                } else {
                    this.toast.error(res.data.message || 'Eroare la adăugarea reminder-ului.');
                }
            } catch (e) {
                this.toast.error('Eroare la adăugarea reminder-ului.');
                console.error(e);
            } finally {
                this.reminderSaving = false;
            }
        },
        async toggleReminder(config) {
            try {
                const jwtToken = localStorage.getItem('userToken');
                await axios.post(
                    '/api/index.php?endpoint=saveReminderConfig',
                    {
                        project_id: this.currentProjectId,
                        action: 'upsert',
                        config_id: config.id,
                        days_before: config.days_before,
                        channel: config.channel,
                        enabled: !config.enabled,
                    },
                    { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwtToken}` } }
                );
                config.enabled = !config.enabled;
                this.toast.success(config.enabled ? 'Reminder activat.' : 'Reminder dezactivat.');
            } catch (e) {
                this.toast.error('Eroare la actualizarea reminder-ului.');
                console.error(e);
            }
        },
        deleteReminder(config) {
            this.reminderToDelete = config;
        },
        async confirmDeleteReminder() {
            if (!this.reminderToDelete) return;
            this.reminderSaving = true;
            try {
                const jwtToken = localStorage.getItem('userToken');
                const res = await axios.post(
                    '/api/index.php?endpoint=saveReminderConfig',
                    {
                        project_id: this.currentProjectId,
                        action: 'delete',
                        config_id: this.reminderToDelete.id,
                    },
                    { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwtToken}` } }
                );
                if (res.data.success) {
                    this.toast.success('Reminder șters.');
                    this.reminderConfigs = this.reminderConfigs.filter(c => c.id !== this.reminderToDelete.id);
                }
            } catch (e) {
                this.toast.error('Eroare la ștergerea reminder-ului.');
                console.error(e);
            } finally {
                this.reminderSaving = false;
                this.reminderToDelete = null;
            }
        },
        getReminderSendDate(config) {
            if (!this.scopedProjectData || !this.scopedProjectData.project_date) return null;
            const eventDate = new Date(this.scopedProjectData.project_date);
            const sendDate = new Date(eventDate);
            sendDate.setDate(sendDate.getDate() - config.days_before);
            const months = ['Ian', 'Feb', 'Mar', 'Apr', 'Mai', 'Iun', 'Iul', 'Aug', 'Sep', 'Oct', 'Noi', 'Dec'];
            return `${sendDate.getDate()} ${months[sendDate.getMonth()]} ${sendDate.getFullYear()}`;
        },
    },
};
</script>

<style scoped>
.pd { min-height: 100vh; background: #f8fafc; }

.pd-container {
    max-width: 1280px;
    width: 100%;
    margin: 0 auto;
    padding: 0 20px 60px;
    text-align: left;
}

/* ── Back link ── */
.pd-back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: #64748b;
    text-decoration: none;
    padding: 24px 0 16px;
    transition: color 0.15s;
}
.pd-back:hover { color: #0f172a; }

/* ── Project header ── */
.pd-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 28px;
}
.pd-header__name {
    font-size: 28px;
    font-weight: 800;
    color: #0f172a;
    margin: 0 0 8px;
    line-height: 1.2;
}
.pd-header__meta {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}
.pd-meta-tag {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
    background: #f1f5f9;
    padding: 4px 10px;
    border-radius: 8px;
}
.pd-meta-tag--type {
    background: #ede9fe;
    color: #7c3aed;
}

/* Rainbow button */
.pd-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 10px 20px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 700;
    border: none;
    cursor: pointer;
    text-decoration: none;
    transition: all 0.15s;
    white-space: nowrap;
    flex-shrink: 0;
}
.pd-btn--rainbow {
    background: linear-gradient(135deg, #6366f1, #8b5cf6, #a855f7, #d946ef, #ec4899, #f43f5e, #f97316, #eab308, #22c55e, #06b6d4, #3b82f6, #6366f1);
    background-size: 300% 300%;
    animation: pdRainbow 4s ease infinite;
    color: #fff;
    box-shadow: 0 2px 12px rgba(139, 92, 246, 0.3);
}
.pd-btn--rainbow:hover {
    box-shadow: 0 4px 20px rgba(139, 92, 246, 0.45);
    transform: translateY(-1px);
    color: #fff;
}
@keyframes pdRainbow {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
}
.pd-btn--ghost {
    background: #f1f5f9;
    color: #475569;
}
.pd-btn--ghost:hover { background: #e2e8f0; color: #0f172a; }
.pd-btn--copy {
    background: #0f172a;
    color: #fff;
    flex-shrink: 0;
}
.pd-btn--copy:hover { background: #1e293b; }

/* ═══════════════════════════════════════════════
   Link card (simple projects)
   ═══════════════════════════════════════════════ */
.pd-link-card {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    padding: 28px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.04);
    border-left: 4px solid #2563eb;
    margin-bottom: 16px;
}
.pd-link-card__header {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 18px;
    padding-bottom: 16px;
    border-bottom: 1px solid #f1f5f9;
}
.pd-link-card__icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: #dbeafe;
    color: #2563eb;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    flex-shrink: 0;
}
.pd-link-card__title {
    font-size: 17px;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 2px;
}
.pd-link-card__desc {
    font-size: 13px;
    color: #64748b;
    margin: 0;
}
.pd-link-card__body {
    display: flex;
    align-items: center;
    gap: 12px;
}
.pd-link-card__url {
    flex: 1;
    padding: 10px 14px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    font-size: 13px;
    color: #2563eb;
    word-break: break-all;
    font-family: monospace;
}

/* Draft banner */
.pd-draft-banner {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 18px 22px;
    background: #fffbeb;
    border: 1.5px solid #fde68a;
    border-radius: 14px;
    margin-bottom: 16px;
}
.pd-draft-banner__icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: #fef3c7;
    color: #d97706;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    flex-shrink: 0;
}
.pd-draft-banner__content { flex: 1; text-align: left; }
.pd-draft-banner__title {
    font-size: 15px;
    font-weight: 700;
    color: #92400e;
    margin: 0 0 4px;
}
.pd-draft-banner__desc {
    font-size: 13px;
    color: #a16207;
    margin: 0;
    line-height: 1.5;
}
.pd-draft-banner__btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 10px 20px;
    border-radius: 10px;
    background: #d97706;
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    text-decoration: none;
    white-space: nowrap;
    transition: background 0.2s;
    flex-shrink: 0;
}
.pd-draft-banner__btn:hover { background: #b45309; }

/* Disabled link card */
.pd-link-card--disabled {
    opacity: 0.55;
    pointer-events: none;
    position: relative;
}

/* ═══════════════════════════════════════════════
   Navigation cards
   ═══════════════════════════════════════════════ */
.pd-cards__row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-bottom: 20px;
}
.pd-card {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 22px 24px;
    background: #fff;
    border: 1.5px solid #e2e8f0;
    border-radius: 14px;
    text-decoration: none;
    transition: all 0.18s;
    box-shadow: 0 1px 3px rgba(0,0,0,0.04);
    cursor: pointer;
}
.pd-card:hover {
    border-color: #7c3aed;
    box-shadow: 0 4px 16px rgba(124, 58, 237, 0.1);
    transform: translateY(-2px);
}
.pd-card--primary {
    background: linear-gradient(135deg, #eff6ff 0%, #f0f0ff 100%);
    border-color: #bfdbfe;
    padding: 26px;
}
.pd-card--primary:hover {
    border-color: #3b82f6;
    box-shadow: 0 4px 20px rgba(59, 130, 246, 0.15);
}
.pd-card--secondary {
    margin-top: 8px;
}
.pd-card__icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    flex-shrink: 0;
}
.pd-card__icon--blue { background: #dbeafe; color: #2563eb; }
.pd-card__icon--slate { background: #f1f5f9; color: #475569; }
.pd-card__icon--green { background: #dcfce7; color: #16a34a; }
.pd-card__content {
    flex: 1;
    min-width: 0;
}
.pd-card__title {
    font-size: 16px;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 3px;
}
.pd-card__desc {
    font-size: 13px;
    color: #64748b;
    margin: 0;
    line-height: 1.4;
}
.pd-card__arrow {
    color: #cbd5e1;
    font-size: 13px;
    flex-shrink: 0;
    transition: color 0.15s, transform 0.15s;
}
.pd-card:hover .pd-card__arrow {
    color: #7c3aed;
    transform: translateX(2px);
}
.pd-card--primary:hover .pd-card__arrow {
    color: #3b82f6;
}

/* ═══════════════════════════════════════════════
   Send section
   ═══════════════════════════════════════════════ */
.pd-send {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    padding: 28px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.04);
    border-left: 4px solid #7c3aed;
    margin-bottom: 16px;
}
.pd-send__header {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 22px;
    padding-bottom: 18px;
    border-bottom: 1px solid #f1f5f9;
}
.pd-send__icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: #ede9fe;
    color: #7c3aed;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    flex-shrink: 0;
}
.pd-send__title {
    font-size: 17px;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 2px;
}
.pd-send__desc {
    font-size: 13px;
    color: #64748b;
    margin: 0;
}
.pd-send__warning {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px 16px;
    margin-bottom: 16px;
    border-radius: 10px;
    background: #fef3c7;
    border: 1px solid #fbbf24;
    font-size: 13px;
    color: #92400e;
    line-height: 1.5;
}
.pd-send__warning-icon {
    flex-shrink: 0;
    margin-top: 2px;
    color: #d97706;
}
.pd-send__grid {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 12px;
}
.pd-send__col {
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.pd-send__card {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 16px 18px;
    border-radius: 12px;
    border: 1.5px solid #e2e8f0;
    background: #fff;
    cursor: pointer;
    transition: all 0.15s;
    text-align: left;
}
.pd-send__card:hover:not(:disabled) {
    border-color: #7c3aed;
    box-shadow: 0 2px 8px rgba(124,58,237,0.08);
}
.pd-send__card:disabled {
    opacity: 0.45;
    cursor: not-allowed;
}
.pd-send__card-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    flex-shrink: 0;
}
.pd-send__card-icon--email { background: #dbeafe; color: #2563eb; }
.pd-send__card-icon--sms { background: #dcfce7; color: #16a34a; }
.pd-send__card-icon--whatsapp { background: #dcfce7; color: #25d366; }
.pd-send__card-body { flex: 1; min-width: 0; }
.pd-send__card-title {
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
    margin-bottom: 1px;
}
.pd-send__card-hint {
    font-size: 12px;
    color: #64748b;
}
.pd-send__card-hint--locked {
    font-size: 11px;
    color: #f59e0b;
    font-weight: 600;
}
.pd-send__badge--free {
    background: #dcfce7;
    color: #16a34a;
    font-size: 11px;
    font-weight: 700;
}
.pd-send__badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 10px;
    border-radius: 20px;
    background: #fef3c7;
    color: #d97706;
    font-size: 12px;
    font-weight: 700;
    flex-shrink: 0;
}
.pd-send__arrow {
    color: #94a3b8;
    font-size: 12px;
    flex-shrink: 0;
}
.pd-customize {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 0;
    background: none;
    border: none;
    font-size: 12px;
    font-weight: 600;
    color: #7c3aed;
    cursor: pointer;
    transition: color 0.15s;
    align-self: flex-start;
    margin-left: 2px;
}
.pd-customize:hover { color: #6d28d9; text-decoration: underline; }

/* ═══════════════════════════════════════════════
   Notice
   ═══════════════════════════════════════════════ */
.pd-card__icon--amber { background: #fef3c7; color: #d97706; }
.pd-card--secondary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}
.pd-card--secondary:disabled:hover {
    border-color: #e2e8f0;
    box-shadow: 0 1px 3px rgba(0,0,0,0.04);
    transform: none;
}

.pd-notice {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px 16px;
    border-radius: 10px;
    font-size: 13px;
    line-height: 1.5;
    background: #fffbeb;
    border: 1px solid #fef3c7;
    color: #92400e;
    margin-bottom: 16px;
}
.pd-notice--gdpr {
    background: #f0f9ff;
    border-color: #bae6fd;
    color: #0c4a6e;
    margin-top: 12px;
    padding: 18px 20px;
}
.pd-notice__icon {
    font-size: 18px;
    flex-shrink: 0;
    margin-top: 2px;
}
.pd-notice__text {
    margin: 4px 0 0;
    font-size: 12px;
    color: #64748b;
    line-height: 1.5;
}

/* ═══════════════════════════════════════════════
   Modal headers
   ═══════════════════════════════════════════════ */
.pd-modal__header {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 24px;
    padding-bottom: 18px;
    border-bottom: 1px solid #f1f5f9;
}
.pd-modal__icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    flex-shrink: 0;
}
.pd-modal__icon--email { background: #dbeafe; color: #2563eb; }
.pd-modal__icon--sms { background: #dcfce7; color: #16a34a; }
.pd-modal__icon--whatsapp { background: #dcfce7; color: #25d366; }
.pd-modal__title {
    font-size: 17px;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 2px;
    text-align: left;
}
.pd-modal__desc {
    font-size: 13px;
    color: #64748b;
    margin: 0;
    text-align: left;
}
.pd-modal__test {
    margin-top: 20px;
    padding-top: 18px;
    border-top: 1px solid #f1f5f9;
}

/* ═══════════════════════════════════════════════
   Reminders section
   ═══════════════════════════════════════════════ */
.pd-remind {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    padding: 28px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.04);
    margin-bottom: 16px;
}
.pd-remind__header {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 24px;
    padding-bottom: 20px;
    border-bottom: 1px solid #f1f5f9;
}
.pd-remind__icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    flex-shrink: 0;
    background: #fef3c7;
    color: #d97706;
}
.pd-remind__header-text { flex: 1; min-width: 0; }
.pd-remind__title {
    font-size: 17px;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 2px;
}
.pd-remind__desc {
    font-size: 13px;
    color: #64748b;
    margin: 0;
}
.pd-remind__loading {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #64748b;
    padding: 12px 0;
}

/* Add form */
.pd-remind__add {
    margin-bottom: 20px;
}
.pd-remind__add-row {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}
.pd-remind__field {
    display: flex;
    align-items: center;
    gap: 8px;
}
.pd-remind__label {
    font-size: 13px;
    font-weight: 600;
    color: #475569;
    white-space: nowrap;
}

/* Credit estimate */
.pd-remind__estimate {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 14px;
    padding: 10px 14px;
    border-radius: 10px;
    background: #fffbeb;
    border: 1px solid #fef3c7;
    font-size: 13px;
    color: #92400e;
    line-height: 1.5;
}
.pd-remind__estimate--ok {
    background: #f0fdf4;
    border-color: #bbf7d0;
    color: #166534;
}
.pd-remind__estimate-icon {
    flex-shrink: 0;
    margin-top: 2px;
    color: #d97706;
}

/* Existing reminders list */
.pd-remind__list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 16px;
}
.pd-remind__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 16px;
    border-radius: 12px;
    border: 1.5px solid #e2e8f0;
    background: #fafbfc;
    transition: border-color 0.15s;
}
.pd-remind__item:hover {
    border-color: #cbd5e1;
}
.pd-remind__item-info {
    display: flex;
    align-items: center;
    gap: 14px;
    flex-wrap: wrap;
    min-width: 0;
}
.pd-remind__item-timing {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    color: #0f172a;
}
.pd-remind__item-clock {
    color: #d97706;
    font-size: 13px;
}
.pd-remind__channel-badge {
    display: inline-flex;
    align-items: center;
    padding: 2px 10px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}
.pd-remind__channel-badge--email { background: #dbeafe; color: #2563eb; }
.pd-remind__channel-badge--sms { background: #dcfce7; color: #16a34a; }
.pd-remind__channel-badge--both { background: #ede9fe; color: #7c3aed; }
.pd-remind__item-date {
    font-size: 12px;
    color: #94a3b8;
}
.pd-remind__item-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
}
.pd-remind__toggle {
    display: flex;
    align-items: center;
}

/* Info text */
.pd-remind__info {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: #64748b;
    line-height: 1.5;
    margin: 0;
}

/* ── Loading ── */
.pd-loading {
    position: fixed;
    inset: 0;
    background: rgba(255,255,255,0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 50;
}

/* ── Mobile ── */
@media (max-width: 768px) {
    .pd-container { padding: 0 16px 40px; }
    .pd-header { flex-direction: column; gap: 12px; }
    .pd-header__name { font-size: 22px; }
    .pd-cards__row { grid-template-columns: 1fr; }
    .pd-send__grid { grid-template-columns: 1fr; }
    .pd-card { padding: 18px; }
    .pd-card--primary { padding: 22px; }
    .pd-send { padding: 20px 18px; }
    .pd-remind { padding: 20px 18px; }
    .pd-remind__add-row { flex-direction: column; align-items: flex-start; gap: 10px; }
    .pd-remind__item { flex-direction: column; align-items: flex-start; }
    .pd-remind__item-actions { align-self: flex-end; }
    .pd-draft-banner { flex-direction: column; text-align: center; gap: 12px; }
    .pd-draft-banner__content { text-align: center; }
    .pd-draft-banner__icon { margin: 0 auto; }
}
</style>
